import LocaleResolver from "csi/config/LocaleResolver";
import Key from "csi/core/Key";
import def from "csi/def";
import RendererConstructor, { Renderer } from "csi/heading/Renderer";
import locale from "csi/locale";
import csi from "csi/main";
import ReflectionAttribute from "csi/reflection/ReflectionAttribute";
import ReflectionClass from "csi/reflection/ReflectionClass";
import { getClassAttribute } from "csi/reflection/reflection";
import _ProcessorConstructor, { _Processor } from "csi/table/matrix/_Processor";
import types from "csi/types";
import util from "csi/util";
import { isDefined, isNullOrUndefined } from "csi/utility/coreUtility";
import { PATH_OP_INDEX, pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import lang from "dojo/_base/lang";
import { C8AttributeId, C8NodeType } from "src/c8-platform/type/node";
import { C8AttributeKey, C8AttributeType, isRefValueType } from "src/c8-platform/type/value";
import { Action } from "./Action";
import { newAction } from "./ActionFactory";
import ActionsConstructor, { Actions } from "./Actions";
import { Context } from "./Context";
import Matrix from "./Matrix";
import RelationshipConstructor, { Relationship } from "./Relationship";
import RuleConstructor, { Rule } from "./Rule";

type DrawFunction = (ctx: Context, h: Heading, refNode: HTMLElement, ren: Renderer) => void;
type ParserFunction = (h: Heading, element: Element) => void;

let _defaultMatrixClass: typeof _ProcessorConstructor = _ProcessorConstructor;

class Heading {
	static registerDefaultMatrixProcessor(defaultMatrixClass: typeof _ProcessorConstructor): void {
		_defaultMatrixClass = defaultMatrixClass;
	}

	/** indicates heading can or cannot be copied using drag and drop (drag-fill) */
	bCopy?: boolean;

	/** indicate if Heading cannot be removed from view in custom views */
	// TODO: rename to "requiredInView"?
	bFixedKey?: boolean;

	/** indicates if should be made visible in view even when hidden in model */
	bForceVisible?: boolean;

	/** indicates if name should be shown as placeholder text when value is empty (and editable) */
	bPlaceholder?: boolean;

	/** class to use as renderer for Heading to override default */
	classRenderer?: string | typeof RendererConstructor;

	/** optional parameters to pass to the RendererClass */
	classRendererParams?: Partial<Renderer>;

	/** specifies column width in table views to override the default; can be set to Small, Medium, or Large, or a number in pixels */
	columnWidth?: string | number;

	/** list of original configured sub-headings */
	configuredSubHeadings?: Heading[];

	/** node type where Heading defined */
	definedOnNodeType?: C8NodeType;

	/** array of dependency paths needed for rendering Heading - currently relative to Heading m_path */
	depPaths?: string[];

	/** if specified, render label with separator before heading value */
	labelSeparator?: string;

	/** matrix configuration */
	matrix?: Matrix;

	/** 
	 * action for heading by node type
	 * NOTE: ideally this would be resolved and owned by Spec when created rather than resolved on fly and stored at Heading level
	 */
	rendererActionByType: Record<C8NodeType, Action> = {};

	/** typed key for heading populated from SpecBuilder */
	sourceKey?: Key;

	/** value type for attribute/property */
	valueType?: C8AttributeType;

	/** CSS class to style resource */
	m_class?: string;

	/** actions applied to the heading */
	m_defActions?: Actions;

	/** indicates attribute on which this resource's display depends on - triggers refresh automatically when that resource changes */
	m_dependency?: C8AttributeId;

	/** attribute type based format to affect rendering */
	m_format?: string;

	/** attribute id */
	m_id?: C8AttributeId;

	/** index into container attributes (use with "id") */
	m_index?: number | string;

	/** indicates if rendered as anchor, and if not empty then to which view it should anchor to */
	m_link?: string;

	/** the path to the view the anchor is created for */
	linkPath?: string;

	/** key to use for name lookup - should only be used in conjunction with Value and OnDraw properties */
	m_name?: string;

	/** 
	 * name of custom render function of type DrawFunction
	 * @deprecated - use RendererClass instead
	 */
	m_onDraw?: string;

	/** parent heading for sub-headings */
	m_parent?: Heading;

	/** traverse path prior to rendering to get to proper node context */
	m_path?: string;

	/** node property */
	m_prop?: string;

	/** rules to adjust CSS classes applied to resource */
	m_rules?: Rule[];

	/** separator to render between parent heading and sub-heading */
	m_separator?: string;

	/** list of sub-headings */
	m_subheadings?: Heading[];

	/** dynamic value to render - can be expression */
	m_value?: string;

	/** action for editing - overrides default action for editing */
	m_action?: Action;

	/** access right required for editing */
	m_right?: string;

	/**
	 * used for editing ref attributes 
	 * @deprecated - use aggregate store instead
	 */
	m_relationship?: Relationship;

	/** 
	 * indicates if resource requires a value when edited 
	 * @deprecated - specify in attribute definition
	 */
	m_bRequired?: boolean;

	/** matrix processor */
	_matrixProcessor?: _Processor;

	/** used to locate Cards and substitute headings */
	m_bCard?: boolean;

	/** indicates if currently hidden from display in view */
	m_bHidden?: boolean;

	/** alternate label for heading - otherwise use name */
	m_label?: string;

	/** original name of heading before being resolved */
	m_nameOrig?: string;

	/** tooltip string when hovering over resource */
	m_tooltip?: string;

	/** indicates if this heading is explicitly allowed/disallowed to be augmentable from the <Node> headings */
	m_bAugmentable?: boolean;

	/** reflection class owning attribute */
	_rc?: ReflectionClass;

	/** reflection attribute if specifies "id" */
	_rca?: ReflectionAttribute;

	/** element where Heading defined */
	_xn?: Element;

	constructor(element?: Element) {
		if (element) {
			// parse properties that will not be affected when augmenting with Node Heading
			const cardId = util.getProperty(element, "Card");
			this.m_bCard = !!cardId || (element.tagName == "Card");
			this.m_id = cardId || util.getProperty(element, "Id");
			this.m_path = util.getProperty(element, "Path");
			this.m_prop = util.getProperty(element, "Property");
			this.m_value = util.getProperty(element, "Value");
			this.m_bAugmentable = util.getBooleanProperty(element, "Augment");

			// parse all other properties
			_parseHeading(this, element);
		}
	}

	/** Return name configured for Heading (not localized name) */
	get configuredName(): string | undefined {
		return this.isResolved ? this.m_nameOrig : this.m_name;
	}

	/**
	 * Return true if heading should not be rendered in view
	 */
	get isHidden(): boolean {
		const { m_bHidden, isMatrixGroup, m_subheadings } = this;

		if (m_bHidden) {
			return true;
		}

		if (isMatrixGroup) {
			return !m_subheadings || m_subheadings.every((sh) => sh.isHidden);
		}

		return false;
	}

	/** Return true if heading represents a matrix group */
	get isMatrixGroup(): boolean {
		const { matrix } = this;
		return !!matrix && !matrix.flat;
	}

	/** Return id for matrix group if heading is a matrix group */
	get matrixGroupId(): string | undefined {
		const { configuredName, isMatrixGroup, m_id } = this;
		return isMatrixGroup ? (configuredName || m_id) : undefined;
	}

	/** Return path for matrix */
	// TODO: attributeKeys - need to determine if this should be called matrixPath instead? It can be used for ungrouped matrices as well (such as BOM PartMaterialSizes);
	// however, maybe that should not defined a Heading in XML and instead create one dynamically?
	get matrixGroupPath(): string | undefined {
		const attrType = this.getAttrType();

		if (this.matrix && (!attrType || isRefValueType(attrType))) {
			return this.getQueryPath();
		}
	}

	/** Return id for matrix group if sub-heading of a matrix group */
	get parentMatrixGroupId(): string | undefined {
		const { m_parent } = this;
		return m_parent?.parentMatrixGroupId || m_parent?.matrixGroupId;
	}

	/** Return reflection class that owns attribute */
	get rc(): ReflectionClass {
		return this._rc;
	}

	/** Return reflection attribute */
	get rca(): ReflectionAttribute {
		return this._rca;
	}

	/** Return key as string to represent object */
	toString(): string {
		return this.getKey();
	}

	/**
	 * Return true if this heading is augmentable from Node/Headings
	 */
	get isAugmentable(): boolean {
		return this.m_bAugmentable === true ||
			!(
				this.m_bAugmentable === false ||
				this.m_bCard ||
				isDefined(this.m_index) ||
				isDefined(this.m_value) ||
				this.m_onDraw ||
				this.classRenderer ||
				this.classRendererParams ||
				this.columnWidth ||
				this.m_dependency ||
				this.m_separator ||
				this.m_defActions ||
				this.m_relationship ||
				this.m_rules ||
				this.matrix ||
				this.m_subheadings
			);
	}

	/**
	 * Return true if Heading represents a matrix allowing only the configured sub-headings rather than all attributes of a class
	 * Examples: CareInstructionsMatrix, ProductSupplierMatrix, SizeChartDimensionSummaryMatrix
	 */
	get isConfiguredMatrixGroup(): boolean {
		const { configuredSubHeadings, m_id, isMatrixGroup, m_path } = this;
		return isMatrixGroup && !m_id && !m_path && !!configuredSubHeadings?.length;
	}

	/**
	 * Return true if heading represents node's name
	 */
	get isName(): boolean {
		return (this.m_id === xml.ATTR_NAME) || (this.m_prop === xml.PROP_NAME);
	}

	/**
	 * Return true if heading represents node name on current item (no path)
	 */
	get isNameWithoutPath(): boolean {
		return this.isName && !this.m_path;
	}

	/**
	 * Return true if heading has already been resolved
	 */
	get isResolved(): boolean {
		return isDefined(this.m_nameOrig);
	}

	/**
	 * Return true if the current heading is sub heading
	 */
	isSubHeading(): boolean {
		return !!this.m_parent;
	}

	clearSubheadings(): void {
		this.m_subheadings = null;
	}

	/**
	 * Add subheading to heading
	 */
	addSubHeading(subheading: Heading, dynamic?: boolean): void {
		this.m_subheadings ||= [];
		this.m_subheadings.push(subheading);
		subheading.m_parent = this;

		if (!dynamic) {
			this.configuredSubHeadings ||= [];
			this.configuredSubHeadings.push(subheading);
		}
	}

	/**
	 * Special resolve function only for headings since they are resolved specially in base view
	 */
	resolveHeading(lr: LocaleResolver, rca: ReflectionAttribute, rc?: ReflectionClass, rcSource?: ReflectionClass, bForce?: boolean): void {
		if (this.isResolved) {
			if (bForce) {
				// restore original name so that logic will be same as first time through - if there is no original name,
				// then we want the name undefined so that it is resolved again from locale resources
				this.m_name = this.m_nameOrig || undefined;
			} else {
				return;
			}
		}

		const id = this.m_id;

		rc ||= rca?.getOwner();
		rca ||= getClassAttribute(rc, id);

		this.augmentHeading(rc, rcSource);
		this._rc = rc;
		this._rca = rca;

		// localize name and tooltip - tooltip only available for attributes
		const { name, tooltip } = lr.resolveHeadingNames(this.getKey(), rc?.type, id, this.m_prop, this.m_name);

		// store original name before localization and before getting key
		this.m_nameOrig = this.m_name ?? "";
		this.m_name = name;
		this.m_tooltip = tooltip;

		// always show links for Node Name headings
		if (id == xml.ATTR_NAME) {
			this.m_link ||= "";
		}

		// default right for actions on heading is "Write"
		const right = this.m_action?.m_right || this.m_right || xml.RIGHT_WRITE;

		if (rca) {
			// get the additional information from model if not already defined
			this.m_format ||= rca.getFormat();
			this.columnWidth ||= rca.getColumnWidth();
			this.m_bRequired ||= rca.isRequired;
		}

		this.m_right = null;
		this.setRight(right);

		// set defaults for formats
		if (!this.m_format) {
			if (this.m_id == xml.ATTR_TYPE) {
				this.m_format = "Type";
			} else if (rca?.valueType == xml.ATTR_TIME) {
				this.m_format = types.FORMAT_DATE;
			} else if (xml.propertyType(this.m_prop) == xml.ATTR_TIME) {
				this.m_format = types.FORMAT_DATETIME;
			}
		}

		// resolve actions
		this.m_action?.resolve(lr, rc);
		this.m_defActions?.resolve(lr, rc);
	}

	/**
	 * Update Heading with properties from Heading definition under Node/Headings
	 */
	augmentHeading(rc?: ReflectionClass, rcSource?: ReflectionClass): void {
		const { m_id, m_path } = this;

		// only attribute headings are supported right now
		if (!m_id || !this.isAugmentable) {
			return;
		}

		const classHeading = (m_path && def.getClassHeading(rcSource, m_id, this.getClassPath())) || def.getClassHeading(rc, m_id);
		if (classHeading) {
			const [element, type] = classHeading;
			this._xn = element;
			this.definedOnNodeType = type;
			_parseHeading(this, element);

			// if heading is not editable, then action should follow
			if (this.m_action && (this.m_right === xml.RIGHT_NONE)) {
				this.setRight(xml.RIGHT_NONE);
			}
		}

		// track that heading is augmentable
		this.m_bAugmentable = true;
	}

	/**
	 * Return type of attribute from reflection attribute
	 */
	getAttrType(): C8AttributeType | undefined {
		return this.valueType || this._rca?.valueType;
	}

	/**
	 * Return type of class owning attribute
	 */
	get ownerType(): C8NodeType | undefined {
		return this._rc?.type;
	}

	/**
	 * Get key for heading - used for resolving view resources, but is not guaranteed to be unique
	 * @param bReflection - if specified, then disregard Heading name and return key based on model only
	 */
	getKey(bReflection?: boolean): string {
		const {
			configuredName,
			parentMatrixGroupId,
			m_id,
			m_index,
			m_prop,
		} = this;

		const path = this.getClassPath();
		let key: string;

		if (bReflection && (m_id || m_prop)) {
			key = def.getHeadingKey(path, m_id, m_prop, null, null, parentMatrixGroupId);
		} else {
			// if key has not path, id, or property, then allow normal key
			key = def.getHeadingKey(path, m_id, m_prop, configuredName, m_index, parentMatrixGroupId);
		}

		return key;
	}

	/**
	 * Return untyped Key for Heading
	 */
	get untypedKey(): Key {
		const {
			configuredName: name,
			parentMatrixGroupId: matrix,
			m_id: id,
			m_index: index,
			m_prop: prop,
		} = this;

		const path = this.getClassPath();
		
		return new Key({
			id,
			index,
			matrix,
			name,
			path,
			prop,
		});
	}

	/**
	 * Return untyped key string for heading
	 */
	getKeyDP(): C8AttributeKey {
		return this.untypedKey.untypedString;
	}

	/**
	 * get label to be used for heading - in most cases this is just the name, but when there are
	 * duplicate data classes in view, then a prefix is added
	 */
	getLabel(index?: string | number): string {
		let s = this.m_label ?? this.m_name;

		if (isNullOrUndefined(index)) {
			index = this.m_index;
		}

		if (s && isDefined(index)) {
			// if an index is used, then add index as parameter and substitute; in case of vector, increment by 1
			// so index does not start at 0
			const type = this.getAttrType();

			if (type?.indexOf("vector") > 0) {
				index = Number(index) + 1;
			}

			s = locale.stringSubstitute(s, [index.toString()]);
		}

		return s;
	}

	/**
	 * Return true if index and attribute is ref container
	 */
	hasPathIndex(): boolean {
		return (this.m_index != null) && xml.attributeTypeIsRef(this.getAttrType());
	}

	/**
	 * Quick lookup to determine if heading has reflection attribute expression
	 */
	hasRollupExpression(): boolean {
		return !!this._rca?.getExpression();
	}

	isEmpty(): boolean {
		return !this.m_id && !this.m_prop && !this.m_value && !this.m_onDraw;
	}

	/**
	 * Return true if Heading is configured to allow query support (for filtering, grouping, sorting, reporting)
	 */
	canQuery(): boolean {
		const { classRendererParams, isMatrixGroup, m_parent } = this;
		return !isMatrixGroup && !classRendererParams?.restrictQuery && !m_parent?.getMatrixProcessor()?.restrictQuery;
	}

	getTooltip(): string {
		return this.m_tooltip;
	}

	getRight(): string {
		return this.m_action ? this.m_action.m_right : this.m_right;
	}

	setRight(right: string): void {
		if (this.m_action) {
			this.m_action.m_right = right;
		} else {
			this.m_right = right;
		}
	}

	getSubHeading(id: C8AttributeId): Heading {
		return this.m_subheadings?.find((h) => h.m_id === id);
	}

	hasSubHeading(h: Heading): boolean {
		return h.m_parent === this;
	}

	/**
	 * Determine if parent heading is matrix group
	 */
	isMatrixSubHeading(): boolean {
		// summary: determine if parent heading is matrix group with or without columns
		var p = this.m_parent;
		if (!p) {
			return false;
		}

		if (p.isMatrixGroup) {
			return true;
		}

		// handle case where not a matrix group; it is not a matrix group because
		// we don't want to allow multiple attributes in the matrix, but it is still defined like one
		if (p.matrix && !p.isMatrixGroup && (this.m_separator == null)) {
			return true;
		}

		return false;
	}

	/**
	 * Return path relative to relationship results
	 */
	getClassPath(): string {
		const { m_parent, m_path } = this;

		if (this.isMatrixSubHeading()) {
			// if matrix group, then prefix path with parent path and id
			return pathCreate(m_parent.matrixGroupPath, m_path);
		}

		if (m_parent) {
			// if sub-heading, then add parent path as prefix; if separator, then also add parent id
			return (m_parent.m_separator != null) ?
				pathCreate(m_parent.getClassPath(), m_parent.m_id, m_path) :
				pathCreate(m_parent.getClassPath(), m_path);
		}

		return m_path;
	}

	/**
	 * Return path relative to relationship results
	 */
	getQueryPath(): string {
		const { m_id, m_index } = this;
		const valueType = this.getAttrType();
		let path = this.getClassPath();

		if (xml.attributeTypeIsRef(valueType)) {
			path = pathCreate(path, m_id);
		}

		if (path && this.hasPathIndex()) {
			path = pathCreate(path, PATH_OP_INDEX, m_index?.toString());
		}

		return path;
	}

	/**
	 * Create matrix processor if does not exist
	 */
	getMatrixProcessor(): _Processor {
		const { matrix } = this;
		let { _matrixProcessor } = this;

		if (matrix && !_matrixProcessor) {
			const {
				matrixClass = _defaultMatrixClass,
				matrixClassParams,
			} = matrix;

			const ctr = csi.syncRequire(matrixClass);
			if (ctr) {
				this._matrixProcessor = _matrixProcessor = new ctr(matrix);

				if (matrixClassParams) {
					lang.mixin(_matrixProcessor, matrixClassParams);
				}
			} else {
				console.warn("csi/def/Heading#getMatrixProcessor - class not loaded: " + matrixClass);
			}
		}

		return _matrixProcessor;
	}
};

/**
 * Return value for column width given input value
 */
function _getColumnWidth(value: string): string | number | null {
	if (types.COLUMN_WIDTH_RE.test(value)) {
		return value;
	}

	const n = parseInt(value);
	if (n) {
		return Math.max(Math.min(n, types.COLUMN_WIDTH_MAX), types.COLUMN_WIDTH_MIN);
	}

	return null;
}

/**
 * Populate Heading properties from given element
 */
function _parseHeading(h: Heading, obj: Element): void {
	// parseHeading can be called twice on the same Heading (when augmenting View Heading with Node Heading),
	// so in general we need to preserve properties from existing Heading (from View) before overwriting with
	// new values (from Node)
	h.m_onDraw ||= util.getProperty(obj, "OnDraw");
	h.m_index = h.m_index ?? util.getProperty(obj, "Index");

	h.bCopy ||= util.getBooleanProperty(obj, "Copy");
	h.classRenderer ||= util.getProperty(obj, "RendererClass");
	h.classRendererParams = csi.fromJson(util.getProperty(obj, "RendererClassParams"));
	h.columnWidth ||= _getColumnWidth(util.getProperty(obj, "ColumnWidth"));
	h.depPaths = util.getProperties(obj, "DependencyPath", "Path");

	h.m_bHidden ||= util.getBooleanProperty(obj, "Hidden");
	h.m_bRequired ||= util.getBooleanProperty(obj, "Required");
	h.m_class ||= util.getProperty(obj, "Class");
	h.m_dependency ||= util.getProperty(obj, "Dependency");
	h.m_format ||= util.getProperty(obj, "Format");
	h.m_name ||= util.getProperty(obj, "Name");
	h.m_link ||= util.getProperty(obj, "Link");
	h.linkPath ||= util.getProperty(obj, "LinkPath");
	h.m_relationship ||= util.getDef(obj, "Relationship", RelationshipConstructor);
	h.m_right ||= util.getProperty(obj, "Right");
	h.m_rules ||= util.getDefs(obj, "Rule", RuleConstructor);
	h.m_separator ||= util.getProperty(obj, "Separator");

	if (!h.m_subheadings) {
		h.m_subheadings = util.getDefs(obj, "Heading", Heading);
		
		if (h.m_subheadings) {
			h.m_subheadings.forEach((sh) => {
				sh.m_parent = h;
			});
			
			// store reference to originally configured subheadings
			// NOTE: ideally the original headings would not be touched and client would only work with Spec,
			// but for now Headings (and their sub-headings) are still used in many places to represent what
			// is to be displayed in the view based on custom view settings (rather than how they were configured)
			h.configuredSubHeadings = [...h.m_subheadings];
		}
	}

	if (!h.m_defActions) {
		h.m_defActions = util.getDef(obj, "Actions", ActionsConstructor);

		// for each action defined for heading, prefix with heading path and assign back-pointer to heading
		h.m_defActions?.getActions()?.forEach((a) => {
			a.heading = h;
			a.m_additionalPrefixPath = h.m_path;
		});

		// do not overwrite the action if it's already defined
		if (!h.m_action) {
			// find action as child tag of Heading
			const actions = util.getDefs(obj, "child::*", newAction, true);

			h.m_action = actions?.[0];

			if (h.m_action) {
				h.m_action.heading = h;
			}
		}
	}

	// parse matrices
	if (!h.matrix) {
		h.matrix = util.getDef(obj, "Matrix", _parseMatrix, true);

		const bMatrixGroup = util.getBooleanProperty(obj, "IsMatrixGroup");
		if (bMatrixGroup && !h.matrix) {
			h.matrix = {};
		}
	}
}

/**
 * Create Matrix from given element
 */
function _parseMatrix(obj: Element): Matrix {
	const flat = !util.getBooleanProperty(obj, "Group", true);
	const columnHeading = util.getDef(obj, "ColumnHeading", Heading);
	const columnTargetType = util.getProperty(obj, "Target");
	const columnNodeHeading = columnTargetType ? util.getDef(obj, "ColumnNodeHeading", Heading) : null;
	const matrixClass = util.getProperty(obj, "MatrixClass");
	const matrixClassParams = csi.fromJson(util.getProperty(obj, "MatrixClassParams"));
	const parentPathScope = util.getProperty(obj, "ParentPathScope");

	return {
		columnHeading,
		columnNodeHeading,
		columnTargetType,
		flat,
		matrixClass,
		matrixClassParams,
		parentPathScope,
	};
}

export = Heading;
